import type { PatrolServicePlan, RequiredVisit } from '../types';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const CRITICALITY_STYLES: Record<string, string> = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  required: 'bg-amber-50 text-amber-700 border-amber-200',
  routine: 'bg-slate-50 text-slate-600 border-slate-200',
  optional: 'bg-slate-50 text-slate-400 border-slate-200',
};

function fmtTime(t: string | null | undefined) {
  if (!t) return '';
  return t.slice(0, 5);
}

function fmtWindow(v: RequiredVisit) {
  if (v.time_window_type === 'fixed') return `at ${fmtTime(v.time_window_start)}`;
  const s = fmtTime(v.time_window_start);
  const e = fmtTime(v.time_window_end);
  if (v.time_window_type === 'random') return `random ${s}–${e}`;
  return `${s}–${e}`;
}

function fmtDays(days: number[]) {
  if (days.length === 7) return 'Daily';
  if (days.length === 5 && days.every((d) => d >= 0 && d <= 4)) return 'Weekdays';
  if (days.length === 2 && days.includes(5) && days.includes(6)) return 'Weekends';
  return days.map((d) => DAY_LABELS[d]).join(', ');
}

/**
 * Read-only presentation of a patrol plan.  Shared by the vendor site
 * detail page (with an Acknowledge button rendered by the parent) and
 * the admin site detail page (view-only).
 */
export default function PatrolPlanCard({ plan }: { plan: PatrolServicePlan }) {
  const visits = [...plan.required_visits].sort(
    (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
  );

  return (
    <div className="space-y-3">
      {plan.notes && (
        <p className="text-sm text-slate-700 whitespace-pre-wrap">{plan.notes}</p>
      )}

      {visits.length === 0 ? (
        <p className="text-sm text-slate-500 italic">
          No required visits have been defined for this plan yet.
        </p>
      ) : (
        <div className="space-y-2">
          {visits.map((v) => (
            <div
              key={v.id ?? `${v.visit_type}-${v.display_order}`}
              className="rounded-lg border border-slate-200 bg-white p-3"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-slate-900">
                  {v.label || v.visit_type.replace(/_/g, ' ')}
                </span>
                <span
                  className={`text-[11px] px-1.5 py-0.5 rounded border capitalize ${
                    CRITICALITY_STYLES[v.criticality] ?? CRITICALITY_STYLES.required
                  }`}
                >
                  {v.criticality}
                </span>
              </div>
              <div className="text-xs text-slate-600 space-y-0.5">
                <div>
                  <span className="text-slate-400">When:</span>{' '}
                  {fmtDays(v.days_of_week)} · {fmtWindow(v)}
                  {v.frequency_per_day > 1 && ` · ${v.frequency_per_day}×/day`}
                </div>
                {v.expected_duration_min_minutes != null && (
                  <div>
                    <span className="text-slate-400">Duration:</span>{' '}
                    {v.expected_duration_min_minutes}
                    {v.expected_duration_max_minutes != null &&
                      v.expected_duration_max_minutes !== v.expected_duration_min_minutes &&
                      `–${v.expected_duration_max_minutes}`}
                    {' '}min
                  </div>
                )}
                {v.required_tasks.length > 0 && (
                  <div>
                    <span className="text-slate-400">Tasks:</span> {v.required_tasks.join(', ')}
                  </div>
                )}
                {v.required_evidence.length > 0 && (
                  <div>
                    <span className="text-slate-400">Evidence:</span> {v.required_evidence.join(', ')}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
